import React, { useState, useEffect, useCallback } from 'react';
import { formatDate, formatDateTime } from '@/utils/dateFormat';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  FileText, Plus, Search, Upload, Download, Edit, Trash2, 
  History, Eye, RefreshCw, CheckCircle, XCircle, Clock, AlertTriangle, Calendar
} from 'lucide-react';
import api from '@/lib/api';

export default function POPPage() {
  const [pops, setPops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [validadeFilter, setValidadeFilter] = useState('all');
  
  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [showAlertasDialog, setShowAlertasDialog] = useState(false);
  
  const [selectedPop, setSelectedPop] = useState(null);
  const [stats, setStats] = useState(null);
  const [alertas, setAlertas] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    codigo: '',
    titulo: '',
    area: 'Esterilização',
    objetivo: '',
    executantes: '',
    descricao: '',
    orientacoes: '',
    referencias: '',
    motivo_alteracao: '',
    data_validade: ''
  });
  const [selectedFile, setSelectedFile] = useState(null);

  const fetchPops = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (validadeFilter !== 'all') params.vencimento = validadeFilter;
      
      const response = await api.get('/pop', { params });
      setPops(response.data);
    } catch (error) {
      toast.error('Erro ao carregar POPs');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, validadeFilter]);

  const fetchStats = async () => {
    try {
      const response = await api.get('/pop/stats/resumo');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchAlertas = async () => {
    try {
      const response = await api.get('/pop/alertas/vencimento');
      setAlertas(response.data);
    } catch (error) {
      console.error('Error fetching alertas:', error);
    }
  };

  useEffect(() => {
    fetchPops();
    fetchStats();
    fetchAlertas();
  }, [fetchPops]);

  const handleCreate = async () => {
    try {
      const formDataObj = new FormData();
      Object.keys(formData).forEach(key => {
        if (formData[key] && key !== 'motivo_alteracao') {
          formDataObj.append(key, formData[key]);
        }
      });
      if (selectedFile) {
        formDataObj.append('arquivo', selectedFile);
      }

      await api.post('/pop', formDataObj, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('POP criado com sucesso!');
      setShowCreateDialog(false);
      resetForm();
      fetchPops();
      fetchStats();
      fetchAlertas();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar POP');
    }
  };

  const handleUpdate = async () => {
    if (!formData.motivo_alteracao) {
      toast.error('Informe o motivo da alteração');
      return;
    }

    try {
      const formDataObj = new FormData();
      Object.keys(formData).forEach(key => {
        if (formData[key]) {
          formDataObj.append(key, formData[key]);
        }
      });
      if (selectedFile) {
        formDataObj.append('arquivo', selectedFile);
      }

      await api.put(`/pop/${selectedPop.id}`, formDataObj, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('POP atualizado! Nova versão criada.');
      setShowEditDialog(false);
      resetForm();
      fetchPops();
      fetchAlertas();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar POP');
    }
  };

  const handleImport = async () => {
    if (!selectedFile || !formData.codigo || !formData.titulo) {
      toast.error('Preencha código, título e selecione o arquivo');
      return;
    }

    try {
      const formDataObj = new FormData();
      formDataObj.append('arquivo', selectedFile);
      formDataObj.append('codigo', formData.codigo);
      formDataObj.append('titulo', formData.titulo);
      formDataObj.append('area', formData.area || 'Esterilização');

      await api.post('/pop/importar', formDataObj, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('POP importado com sucesso!');
      setShowImportDialog(false);
      resetForm();
      fetchPops();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao importar POP');
    }
  };

  const handleDelete = async (pop) => {
    if (!window.confirm(`Deseja revogar o POP "${pop.codigo} - ${pop.titulo}"?`)) return;
    
    try {
      await api.delete(`/pop/${pop.id}`);
      toast.success('POP revogado');
      fetchPops();
      fetchStats();
    } catch (error) {
      toast.error('Erro ao revogar POP');
    }
  };

  const handleRestore = async (pop) => {
    try {
      await api.post(`/pop/${pop.id}/restaurar`);
      toast.success('POP restaurado');
      fetchPops();
      fetchStats();
    } catch (error) {
      toast.error('Erro ao restaurar POP');
    }
  };

  const handleDownload = async (pop) => {
    try {
      const response = await api.get(`/pop/${pop.id}/download`);
      const { arquivo_base64, arquivo_nome } = response.data;
      
      const link = document.createElement('a');
      link.href = `data:application/octet-stream;base64,${arquivo_base64}`;
      link.download = arquivo_nome;
      link.click();
      
      toast.success('Download iniciado');
    } catch (error) {
      toast.error('Erro ao baixar arquivo');
    }
  };

  const resetForm = () => {
    setFormData({
      codigo: '',
      titulo: '',
      area: 'Esterilização',
      objetivo: '',
      executantes: '',
      descricao: '',
      orientacoes: '',
      referencias: '',
      motivo_alteracao: '',
      data_validade: ''
    });
    setSelectedFile(null);
    setSelectedPop(null);
  };

  const openEditDialog = (pop) => {
    setSelectedPop(pop);
    setFormData({
      codigo: pop.codigo,
      titulo: pop.titulo,
      area: pop.area,
      objetivo: pop.objetivo,
      executantes: pop.executantes,
      descricao: pop.descricao,
      orientacoes: pop.orientacoes || '',
      referencias: pop.referencias || '',
      motivo_alteracao: '',
      data_validade: pop.data_validade ? pop.data_validade.split('T')[0] : ''
    });
    setShowEditDialog(true);
  };

  // Helper para badge de validade
  const getValidadeBadge = (info_validade) => {
    if (!info_validade) return null;
    
    const { status, dias_restantes } = info_validade;
    
    if (status === 'vencido') {
      return (
        <Badge variant="destructive" className="flex items-center gap-1">
          <AlertTriangle className="w-3 h-3" />
          Vencido há {Math.abs(dias_restantes)} dias
        </Badge>
      );
    }
    if (status === 'proximo_vencimento') {
      return (
        <Badge variant="warning" className="bg-yellow-500 text-white flex items-center gap-1">
          <Clock className="w-3 h-3" />
          Vence em {dias_restantes} dias
        </Badge>
      );
    }
    if (status === 'vigente') {
      return (
        <Badge variant="outline" className="text-green-600 border-green-300 flex items-center gap-1">
          <CheckCircle className="w-3 h-3" />
          {dias_restantes} dias restantes
        </Badge>
      );
    }
    return null;
  };

  const filteredPops = pops.filter(pop => 
    pop.codigo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    pop.titulo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    pop.area?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="pop-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">
            Procedimentos Operacionais Padrão (POP)
          </h1>
          <p className="text-slate-500">Gerencie e acompanhe as versões dos POPs</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={() => setShowImportDialog(true)}
            data-testid="btn-import-pop"
          >
            <Upload className="w-4 h-4 mr-2" />
            Importar
          </Button>
          <Button 
            onClick={() => { resetForm(); setShowCreateDialog(true); }}
            data-testid="btn-create-pop"
          >
            <Plus className="w-4 h-4 mr-2" />
            Novo POP
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <FileText className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total}</p>
                  <p className="text-xs text-slate-500">Total de POPs</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.vigentes}</p>
                  <p className="text-xs text-slate-500">Vigentes</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <XCircle className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.revogados}</p>
                  <p className="text-xs text-slate-500">Revogados</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <History className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.por_area?.length || 0}</p>
                  <p className="text-xs text-slate-500">Áreas</p>
                </div>
              </div>
            </CardContent>
          </Card>
          {/* Card de Alertas */}
          <Card 
            className={`cursor-pointer transition-all ${stats.alertas?.total_alertas > 0 ? 'border-red-300 bg-red-50 hover:bg-red-100' : ''}`}
            onClick={() => stats.alertas?.total_alertas > 0 && setShowAlertasDialog(true)}
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${stats.alertas?.total_alertas > 0 ? 'bg-red-200' : 'bg-yellow-100'}`}>
                  <AlertTriangle className={`w-5 h-5 ${stats.alertas?.total_alertas > 0 ? 'text-red-600' : 'text-yellow-600'}`} />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.alertas?.total_alertas || 0}</p>
                  <p className="text-xs text-slate-500">Alertas Validade</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Buscar por código, título ou área..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="vigente">Vigentes</SelectItem>
                <SelectItem value="revogado">Revogados</SelectItem>
              </SelectContent>
            </Select>
            <Select value={validadeFilter} onValueChange={setValidadeFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Validade" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas Validades</SelectItem>
                <SelectItem value="vencido">Vencidos</SelectItem>
                <SelectItem value="proximo">Próx. Vencimento</SelectItem>
                <SelectItem value="vigente">Em Dia</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* POPs List */}
      <Card>
        <CardHeader>
          <CardTitle>Lista de POPs ({filteredPops.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">Carregando...</div>
          ) : filteredPops.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              Nenhum POP encontrado. Clique em "Novo POP" ou "Importar" para começar.
            </div>
          ) : (
            <div className="space-y-3">
              {filteredPops.map((pop) => (
                <div 
                  key={pop.id}
                  className={`p-4 border rounded-lg ${
                    pop.status === 'revogado' 
                      ? 'bg-red-50 border-red-200' 
                      : pop.info_validade?.status === 'vencido'
                        ? 'bg-red-50 border-red-300'
                        : pop.info_validade?.status === 'proximo_vencimento'
                          ? 'bg-yellow-50 border-yellow-300'
                          : 'bg-white hover:bg-slate-50'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <span className="font-mono font-bold text-blue-600">{pop.codigo}</span>
                        <Badge variant={pop.status === 'vigente' ? 'default' : 'destructive'}>
                          {pop.status === 'vigente' ? 'Vigente' : 'Revogado'}
                        </Badge>
                        <Badge variant="outline">v{pop.versao_atual}</Badge>
                        {getValidadeBadge(pop.info_validade)}
                      </div>
                      <h3 className="font-semibold text-slate-800">{pop.titulo}</h3>
                      <p className="text-sm text-slate-500 mt-1">
                        Área: {pop.area} | Criado: {formatDate(pop.data_criacao || pop.created_at)} | 
                        Validade: {formatDate(pop.data_validade)}
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => { setSelectedPop(pop); setShowViewDialog(true); }}
                        title="Visualizar"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => { setSelectedPop(pop); setShowHistoryDialog(true); }}
                        title="Histórico de versões"
                      >
                        <History className="w-4 h-4" />
                      </Button>
                      {pop.arquivo_base64 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDownload(pop)}
                          title="Download"
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                      )}
                      {pop.status === 'vigente' ? (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditDialog(pop)}
                            title="Editar"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(pop)}
                            title="Revogar"
                            className="text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </>
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRestore(pop)}
                          title="Restaurar"
                          className="text-green-500 hover:text-green-700"
                        >
                          <RefreshCw className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Novo POP</DialogTitle>
          </DialogHeader>
          <form onSubmit={(e) => { e.preventDefault(); handleCreate(); }}>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Código *</Label>
                <Input
                  placeholder="POP-001"
                  value={formData.codigo}
                  onChange={(e) => setFormData({...formData, codigo: e.target.value})}
                />
              </div>
              <div>
                <Label>Área *</Label>
                <Input
                  placeholder="Esterilização"
                  value={formData.area}
                  onChange={(e) => setFormData({...formData, area: e.target.value})}
                />
              </div>
            </div>
            <div>
              <Label>Título *</Label>
              <Input
                placeholder="Título do procedimento"
                value={formData.titulo}
                onChange={(e) => setFormData({...formData, titulo: e.target.value})}
              />
            </div>
            <div>
              <Label className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Data de Validade
              </Label>
              <Input
                type="date"
                value={formData.data_validade}
                onChange={(e) => setFormData({...formData, data_validade: e.target.value})}
              />
              <p className="text-xs text-slate-500 mt-1">Data limite para revisão obrigatória do POP</p>
            </div>
            <div>
              <Label>Objetivo *</Label>
              <Textarea
                placeholder="Objetivo do procedimento"
                value={formData.objetivo}
                onChange={(e) => setFormData({...formData, objetivo: e.target.value})}
              />
            </div>
            <div>
              <Label>Executantes *</Label>
              <Input
                placeholder="Quem executa este procedimento"
                value={formData.executantes}
                onChange={(e) => setFormData({...formData, executantes: e.target.value})}
              />
            </div>
            <div>
              <Label>Descrição *</Label>
              <Textarea
                placeholder="Descrição detalhada do procedimento"
                rows={4}
                value={formData.descricao}
                onChange={(e) => setFormData({...formData, descricao: e.target.value})}
              />
            </div>
            <div>
              <Label>Orientações</Label>
              <Textarea
                placeholder="Orientações adicionais"
                value={formData.orientacoes}
                onChange={(e) => setFormData({...formData, orientacoes: e.target.value})}
              />
            </div>
            <div>
              <Label>Arquivo (opcional)</Label>
              <Input
                type="file"
                accept=".docx,.pdf,.doc"
                onChange={(e) => setSelectedFile(e.target.files[0])}
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)}>Cancelar</Button>
            <Button type="submit">Criar POP</Button>
          </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar POP - {selectedPop?.codigo}</DialogTitle>
          </DialogHeader>
          <form onSubmit={(e) => { e.preventDefault(); handleUpdate(); }}>
          <div className="grid gap-4 py-4">
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                <Clock className="w-4 h-4 inline mr-1" />
                Ao salvar, uma nova versão será criada automaticamente (atual: v{selectedPop?.versao_atual})
              </p>
            </div>
            <div>
              <Label>Título</Label>
              <Input
                value={formData.titulo}
                onChange={(e) => setFormData({...formData, titulo: e.target.value})}
              />
            </div>
            <div>
              <Label className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Data de Validade
              </Label>
              <Input
                type="date"
                value={formData.data_validade}
                onChange={(e) => setFormData({...formData, data_validade: e.target.value})}
              />
              <p className="text-xs text-slate-500 mt-1">Data limite para revisão obrigatória do POP</p>
            </div>
            <div>
              <Label>Objetivo</Label>
              <Textarea
                value={formData.objetivo}
                onChange={(e) => setFormData({...formData, objetivo: e.target.value})}
              />
            </div>
            <div>
              <Label>Descrição</Label>
              <Textarea
                rows={4}
                value={formData.descricao}
                onChange={(e) => setFormData({...formData, descricao: e.target.value})}
              />
            </div>
            <div>
              <Label>Orientações</Label>
              <Textarea
                value={formData.orientacoes}
                onChange={(e) => setFormData({...formData, orientacoes: e.target.value})}
              />
            </div>
            <div>
              <Label>Novo arquivo (opcional)</Label>
              <Input
                type="file"
                accept=".docx,.pdf,.doc"
                onChange={(e) => setSelectedFile(e.target.files[0])}
              />
            </div>
            <div>
              <Label className="text-red-600">Motivo da alteração *</Label>
              <Textarea
                placeholder="Descreva o que foi alterado e por quê"
                value={formData.motivo_alteracao}
                onChange={(e) => setFormData({...formData, motivo_alteracao: e.target.value})}
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setShowEditDialog(false)}>Cancelar</Button>
            <Button type="submit">Salvar Nova Versão</Button>
          </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedPop?.codigo} - {selectedPop?.titulo}</DialogTitle>
          </DialogHeader>
          {selectedPop && (
            <div className="space-y-4">
              <div className="flex flex-wrap gap-2">
                <Badge variant={selectedPop.status === 'vigente' ? 'default' : 'destructive'}>
                  {selectedPop.status}
                </Badge>
                <Badge variant="outline">Versão {selectedPop.versao_atual}</Badge>
                {getValidadeBadge(selectedPop.info_validade)}
              </div>
              
              <div className="grid grid-cols-2 gap-4 p-3 bg-slate-50 rounded-lg">
                <div>
                  <Label className="text-slate-500 text-xs">Data de Criação</Label>
                  <p className="font-medium">{formatDate(selectedPop.data_criacao || selectedPop.created_at)}</p>
                </div>
                <div>
                  <Label className="text-slate-500 text-xs">Data de Validade</Label>
                  <p className="font-medium">{formatDate(selectedPop.data_validade)}</p>
                </div>
              </div>
              
              <div>
                <Label className="text-slate-500">Área</Label>
                <p>{selectedPop.area}</p>
              </div>
              
              <div>
                <Label className="text-slate-500">Objetivo</Label>
                <p>{selectedPop.objetivo}</p>
              </div>
              
              <div>
                <Label className="text-slate-500">Executantes</Label>
                <p>{selectedPop.executantes}</p>
              </div>
              
              <div>
                <Label className="text-slate-500">Descrição</Label>
                <p className="whitespace-pre-wrap">{selectedPop.descricao}</p>
              </div>
              
              {selectedPop.orientacoes && (
                <div>
                  <Label className="text-slate-500">Orientações</Label>
                  <p className="whitespace-pre-wrap">{selectedPop.orientacoes}</p>
                </div>
              )}
              
              {selectedPop.arquivo_base64 && (
                <div>
                  <Label className="text-slate-500">Arquivo anexo</Label>
                  <Button variant="outline" size="sm" onClick={() => handleDownload(selectedPop)}>
                    <Download className="w-4 h-4 mr-2" />
                    {selectedPop.arquivo_nome || 'Baixar arquivo'}
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={showHistoryDialog} onOpenChange={setShowHistoryDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Histórico de Versões - {selectedPop?.codigo}</DialogTitle>
          </DialogHeader>
          {selectedPop && (
            <div className="space-y-3">
              {selectedPop.historico_versoes?.slice().reverse().map((versao, idx) => (
                <div key={idx} className="p-4 border rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <Badge variant={idx === 0 ? 'default' : 'outline'}>
                      Versão {versao.versao} {idx === 0 && '(Atual)'}
                    </Badge>
                    <span className="text-sm text-slate-500">
                      {new Date(versao.data).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' })}
                    </span>
                  </div>
                  <p className="text-sm"><strong>Usuário:</strong> {versao.usuario}</p>
                  <p className="text-sm"><strong>Alterações:</strong> {versao.alteracoes}</p>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Importar POP</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div>
              <Label>Código *</Label>
              <Input
                placeholder="POP-001"
                value={formData.codigo}
                onChange={(e) => setFormData({...formData, codigo: e.target.value})}
              />
            </div>
            <div>
              <Label>Título *</Label>
              <Input
                placeholder="Título do procedimento"
                value={formData.titulo}
                onChange={(e) => setFormData({...formData, titulo: e.target.value})}
              />
            </div>
            <div>
              <Label>Área</Label>
              <Input
                placeholder="Esterilização"
                value={formData.area}
                onChange={(e) => setFormData({...formData, area: e.target.value})}
              />
            </div>
            <div>
              <Label>Arquivo *</Label>
              <Input
                type="file"
                accept=".docx,.pdf,.doc"
                onChange={(e) => setSelectedFile(e.target.files[0])}
              />
              <p className="text-xs text-slate-500 mt-1">Formatos aceitos: .docx, .pdf, .doc</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowImportDialog(false)}>Cancelar</Button>
            <Button onClick={handleImport}>
              <Upload className="w-4 h-4 mr-2" />
              Importar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Alertas Dialog */}
      <Dialog open={showAlertasDialog} onOpenChange={setShowAlertasDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Alertas de Validade de POPs
            </DialogTitle>
          </DialogHeader>
          {alertas && (
            <div className="space-y-6">
              {/* POPs Vencidos */}
              {alertas.vencidos?.length > 0 && (
                <div>
                  <h3 className="font-semibold text-red-600 mb-3 flex items-center gap-2">
                    <XCircle className="w-4 h-4" />
                    POPs Vencidos ({alertas.vencidos.length})
                  </h3>
                  <div className="space-y-2">
                    {alertas.vencidos.map((pop) => (
                      <div key={pop.id} className="p-3 bg-red-50 border border-red-200 rounded-lg">
                        <div className="flex justify-between items-start">
                          <div>
                            <span className="font-mono font-bold text-red-700">{pop.codigo}</span>
                            <p className="text-sm font-medium">{pop.titulo}</p>
                            <p className="text-xs text-red-600 mt-1">
                              Vencido há {Math.abs(pop.info_validade?.dias_restantes)} dias
                            </p>
                          </div>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => {
                              setShowAlertasDialog(false);
                              openEditDialog(pop);
                            }}
                          >
                            <Edit className="w-3 h-3 mr-1" />
                            Revisar
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* POPs Próximos do Vencimento */}
              {alertas.proximo_vencimento?.length > 0 && (
                <div>
                  <h3 className="font-semibold text-yellow-600 mb-3 flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Próximos do Vencimento ({alertas.proximo_vencimento.length})
                  </h3>
                  <div className="space-y-2">
                    {alertas.proximo_vencimento.map((pop) => (
                      <div key={pop.id} className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <div className="flex justify-between items-start">
                          <div>
                            <span className="font-mono font-bold text-yellow-700">{pop.codigo}</span>
                            <p className="text-sm font-medium">{pop.titulo}</p>
                            <p className="text-xs text-yellow-600 mt-1">
                              Vence em {pop.info_validade?.dias_restantes} dias
                            </p>
                          </div>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => {
                              setShowAlertasDialog(false);
                              openEditDialog(pop);
                            }}
                          >
                            <Edit className="w-3 h-3 mr-1" />
                            Revisar
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {alertas.vencidos?.length === 0 && alertas.proximo_vencimento?.length === 0 && (
                <div className="text-center py-8 text-slate-500">
                  <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-500" />
                  <p>Nenhum alerta de validade no momento!</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
